/**
 * Treppan Academy - Lesson Viewer
 * Split layout for viewing lesson content with navigation
 * Supports text, video (YouTube/Vimeo/direct), and PDF content
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  BookOpen, ChevronLeft, ChevronRight, Check, Menu, X,
  Clock, FileText, Video, FileImage, GraduationCap, Award,
  Play, Pause, Volume2, VolumeX, Maximize, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  surfaceHover: '#1C2438',
  gold: '#D4AF37',
  goldHover: '#B5952F',
  success: '#10B981',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

const contentTypeIcons = {
  text: FileText,
  video: Video,
  pdf: FileImage
};

/**
 * Video Player Component
 * Supports YouTube, Vimeo, and direct video URLs
 */
const VideoPlayer = ({ videoUrl, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Detect video type and extract ID
  const getVideoEmbed = (url) => {
    if (!url) return null;
    
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return {
        type: 'youtube',
        id: youtubeMatch[1],
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}?rel=0&modestbranding=1`
      };
    }
    
    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) {
      return {
        type: 'vimeo',
        id: vimeoMatch[1],
        embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}?title=0&byline=0&portrait=0`
      };
    }
    
    // Direct video URL (mp4, webm, etc.)
    if (url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)) {
      return {
        type: 'direct',
        url: url
      };
    }
    
    // Generic embed (assume it's an embed URL)
    return {
      type: 'embed',
      embedUrl: url
    };
  };
  
  const videoInfo = getVideoEmbed(videoUrl);
  
  if (!videoInfo) {
    return (
      <div 
        className="rounded-xl p-12 text-center"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <Video className="h-16 w-16 mx-auto mb-4" style={{ color: colors.textMuted }} />
        <p style={{ color: colors.textMuted }}>No video available</p>
      </div>
    );
  }
  
  if (videoInfo.type === 'direct') {
    return (
      <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: '#000' }}>
        <video
          className="w-full aspect-video"
          controls
          controlsList="nodownload"
          poster=""
          data-testid="video-player"
        >
          <source src={videoInfo.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }
  
  // YouTube/Vimeo/Embed iframe
  return (
    <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: '#000' }}>
      <div className="aspect-video">
        <iframe
          src={videoInfo.embedUrl}
          title={title || 'Video Lesson'}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="video-iframe"
        />
      </div>
      
      {/* Video platform badge */}
      <div className="absolute top-4 right-4">
        <Badge 
          className="uppercase text-xs tracking-wider"
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: colors.textHeading
          }}
        >
          {videoInfo.type === 'youtube' ? 'YouTube' : videoInfo.type === 'vimeo' ? 'Vimeo' : 'Video'}
        </Badge>
      </div>
    </div>
  );
};

/**
 * PDF Viewer Component
 */
const PDFViewer = ({ pdfUrl, title }) => {
  if (!pdfUrl) {
    return (
      <div 
        className="rounded-xl p-12 text-center"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <FileImage className="h-16 w-16 mx-auto mb-4" style={{ color: colors.textMuted }} />
        <p style={{ color: colors.textMuted }}>No PDF available</p>
      </div>
    );
  }
  
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
      <div className="flex items-center justify-between p-3" style={{ backgroundColor: colors.surface }}>
        <span className="text-sm font-medium" style={{ color: colors.textHeading }}>
          {title || 'Document'}
        </span>
        <a 
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm hover:underline"
          style={{ color: colors.gold }}
        >
          <ExternalLink className="h-4 w-4" />
          Open in new tab
        </a>
      </div>
      <iframe
        src={pdfUrl}
        title={title || 'PDF Document'}
        className="w-full"
        style={{ height: '600px', backgroundColor: '#fff' }}
        data-testid="pdf-viewer"
      />
    </div>
  );
};

export default function LessonViewer() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchLesson();
  }, [courseId, lessonId]);

  const fetchLesson = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/academy/courses/${courseId}/lessons/${lessonId}?token=${token}`
      );
      const result = await res.json();
      
      if (result.success) {
        setData(result);
      } else {
        toast.error('Lesson not found');
        navigate(`/academy/courses/${courseId}`);
      }
    } catch (error) {
      console.error('Error fetching lesson:', error);
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteLesson = async () => {
    setCompleting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/academy/courses/${courseId}/lessons/${lessonId}/complete?token=${token}`,
        { method: 'POST' }
      );
      const result = await res.json();
      
      if (result.success) {
        toast.success('Lesson completed!');
        
        // Refresh data
        await fetchLesson();
        
        // Navigate to next lesson or course page
        if (data.next_lesson) {
          navigate(`/academy/courses/${courseId}/lessons/${data.next_lesson.id}`);
        } else {
          // Check if all lessons are done and exam exists
          const course = await fetch(`${API_URL}/api/academy/courses/${courseId}?token=${token}`).then(r => r.json());
          if (course.course?.exam) {
            toast.success('All lessons completed! Take the certification exam.');
            navigate(`/academy/courses/${courseId}/exam`);
          } else {
            navigate(`/academy/courses/${courseId}`);
          }
        }
      }
    } catch (error) {
      console.error('Error completing lesson:', error);
      toast.error('Failed to complete lesson');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <BookOpen className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { lesson, course, user_progress, prev_lesson, next_lesson } = data;
  const completedLessons = user_progress?.completed_lessons || [];
  const isCompleted = completedLessons.includes(lessonId);
  const sortedLessons = [...(course.lessons || [])].sort((a, b) => a.order - b.order);
  const currentIndex = sortedLessons.findIndex(l => l.id === lessonId);
  const progressPercent = user_progress?.progress_percent || 0;
  const Icon = contentTypeIcons[lesson.content_type] || FileText;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: colors.background }}>
      {/* Sidebar */}
      <div 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-80 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-0 lg:overflow-hidden'
        }`}
        style={{ backgroundColor: colors.surface, borderRight: `1px solid ${colors.border}` }}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b" style={{ borderColor: colors.border }}>
            <div className="flex items-center justify-between mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(`/academy/courses/${courseId}`)}
                style={{ color: colors.textMuted }}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Course
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden"
                style={{ color: colors.textMuted }}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <h2 
              className="font-semibold line-clamp-2 mb-2"
              style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
            >
              {course.title}
            </h2>
            
            <div className="flex items-center gap-2">
              <Progress 
                value={progressPercent}
                className="flex-1 h-2"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              />
              <span className="text-sm font-medium" style={{ color: colors.gold }}>
                {progressPercent}%
              </span>
            </div>
          </div>
          
          {/* Lessons List */}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 
              className="text-xs uppercase tracking-widest mb-4"
              style={{ color: colors.textMuted }}
            >
              Course Content
            </h3>
            
            <div className="space-y-1">
              {sortedLessons.map((l, index) => {
                const LessonIcon = contentTypeIcons[l.content_type] || FileText;
                const isLessonCompleted = completedLessons.includes(l.id);
                const isCurrent = l.id === lessonId;
                
                return (
                  <div 
                    key={l.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      isCurrent ? 'bg-white/10' : 'hover:bg-white/5'
                    }`}
                    onClick={() => navigate(`/academy/courses/${courseId}/lessons/${l.id}`)}
                    data-testid={`sidebar-lesson-${l.id}`}
                  >
                    <div 
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: isLessonCompleted ? `${colors.success}20` : isCurrent ? `${colors.gold}20` : 'transparent',
                        border: `2px solid ${isLessonCompleted ? colors.success : isCurrent ? colors.gold : colors.border}`
                      }}
                    >
                      {isLessonCompleted ? (
                        <Check className="h-3.5 w-3.5" style={{ color: colors.success }} />
                      ) : (
                        <span className="text-xs" style={{ color: isCurrent ? colors.gold : colors.textMuted }}>
                          {index + 1}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p 
                        className="text-sm truncate"
                        style={{ color: isCurrent ? colors.textHeading : colors.textBody }}
                      >
                        {l.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <LessonIcon className="h-3 w-3" style={{ color: colors.textMuted }} />
                        <span className="text-xs" style={{ color: colors.textMuted }}>
                          {l.duration_minutes} min
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Exam Link */}
            {course.exam_id && (
              <div 
                className="mt-4 p-3 rounded-lg border border-dashed cursor-pointer hover:bg-white/5"
                style={{ borderColor: colors.gold }}
                onClick={() => navigate(`/academy/courses/${courseId}/exam`)}
              >
                <div className="flex items-center gap-3">
                  <Award className="h-5 w-5" style={{ color: colors.gold }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: colors.gold }}>
                      Certification Exam
                    </p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>
                      Complete all lessons to unlock
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <div 
          className="flex items-center justify-between p-4 border-b"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{ color: colors.textMuted }}
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            <div className="flex items-center gap-2">
              <Badge 
                className="uppercase text-xs tracking-widest"
                style={{ 
                  backgroundColor: isCompleted ? `${colors.success}20` : `${colors.gold}20`,
                  color: isCompleted ? colors.success : colors.gold,
                  border: `1px solid ${isCompleted ? colors.success : colors.gold}30`
                }}
              >
                <Icon className="h-3 w-3 mr-1" />
                {lesson.content_type}
              </Badge>
              <span className="text-sm" style={{ color: colors.textMuted }}>
                Lesson {currentIndex + 1} of {sortedLessons.length}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" style={{ color: colors.textMuted }} />
            <span className="text-sm" style={{ color: colors.textMuted }}>
              {lesson.duration_minutes} min
            </span>
          </div>
        </div>

        {/* Lesson Content */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8">
          <div className="max-w-3xl mx-auto">
            <h1 
              className="text-3xl font-bold mb-4"
              style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
            >
              {lesson.title}
            </h1>
            
            {lesson.description && (
              <p className="text-lg mb-8" style={{ color: colors.textBody }}>
                {lesson.description}
              </p>
            )}
            
            {/* Video Content */}
            {lesson.content_type === 'video' && (
              <div className="mb-8">
                <VideoPlayer 
                  videoUrl={lesson.video_url || lesson.content}
                  title={lesson.title}
                />
                
                {/* Additional notes below video */}
                {lesson.content && lesson.video_url && (
                  <div 
                    className="mt-6 prose prose-invert prose-lg max-w-none"
                    style={{ color: colors.textBody }}
                    dangerouslySetInnerHTML={{ __html: lesson.content }}
                  />
                )}
              </div>
            )}
            
            {/* PDF Content */}
            {lesson.content_type === 'pdf' && (
              <div className="mb-8">
                <PDFViewer 
                  pdfUrl={lesson.pdf_url || lesson.content}
                  title={lesson.title}
                />
              </div>
            )}
            
            {/* Text Content */}
            {lesson.content_type === 'text' && (
              <div 
                className="prose prose-invert prose-lg max-w-none"
                style={{ color: colors.textBody }}
                dangerouslySetInnerHTML={{ __html: lesson.content || '<p>No content available.</p>' }}
              />
            )}
          </div>
        </div>

        {/* Bottom Navigation */}
        <div 
          className="flex items-center justify-between p-4 border-t"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <Button 
            variant="outline"
            onClick={() => prev_lesson && navigate(`/academy/courses/${courseId}/lessons/${prev_lesson.id}`)}
            disabled={!prev_lesson}
            style={{ borderColor: colors.border, color: colors.textBody }}
            data-testid="prev-lesson-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          
          <div className="flex items-center gap-3">
            {!isCompleted && (
              <Button 
                className="rounded-full font-semibold"
                style={{ backgroundColor: colors.success, color: '#fff' }}
                onClick={handleCompleteLesson}
                disabled={completing}
                data-testid="complete-lesson-btn"
              >
                <Check className="h-4 w-4 mr-2" />
                {completing ? 'Completing...' : 'Mark as Complete'}
              </Button>
            )}
            
            {isCompleted && (
              <Badge 
                className="px-4 py-2"
                style={{ backgroundColor: `${colors.success}20`, color: colors.success }}
              >
                <Check className="h-4 w-4 mr-2" />
                Completed
              </Badge>
            )}
          </div>
          
          <Button 
            onClick={() => {
              if (next_lesson) {
                navigate(`/academy/courses/${courseId}/lessons/${next_lesson.id}`);
              } else {
                navigate(`/academy/courses/${courseId}`);
              }
            }}
            style={{ backgroundColor: colors.gold, color: '#000' }}
            data-testid="next-lesson-btn"
          >
            {next_lesson ? 'Next Lesson' : 'Finish'}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
